import LocalizedTextField from '@components/Customization/LocalizedTextField';
import NumberField from '@components/Customization/NumberField';
import ToggleRow from '@components/Customization/ToggleRow';
import { useLocalization } from '@lib/localization';
import { MenuItem, TextField } from '@mui/material';
import {
  type ReportFieldItem, type ReportImageRef, type ReportSignatureLine, type ReportText,
} from '@store/api/reportDesignEndpoints';
import { type BlockField } from './blockCatalog';
import ImageField, { type DesignImages } from './ImageField';
import { FieldItemsEditor, SignatureLinesEditor } from './ListEditors';

interface Props {
  field: BlockField;
  /** The block's `props` as they are: a key that is absent draws the field's fallback. */
  props: Readonly<Record<string, unknown>>;
  /** Sets keys of the block's `props`; `undefined` takes one off. */
  onPatch: (changes: Record<string, unknown>) => void;
  images: DesignImages;
  disabled?: boolean;
}

/**
 * The control for one field of the block catalog. This is the only place a field's `kind`
 * is read: a new kind is a member of `BlockField` and a case here.
 */
const BlockFieldControl: React.FC<Props> = ({ field, props, onPatch, images, disabled }) => {
  const { t } = useLocalization();

  const value = props[field.key];
  const set = (next: unknown) => onPatch({ [field.key]: next });
  const help = field.help ? t(field.help) : undefined;

  switch (field.kind) {
    case 'text':
      return (
        <LocalizedTextField
          optional
          carriedHint={t('ReportDesign_Text_OtherLanguage')}
          label={t(field.label)}
          value={(value as ReportText | undefined) ?? {}}
          onChange={set}
          multiline={field.multiline}
          maxLength={field.max}
          placeholder={field.placeholder ? t(field.placeholder) : undefined}
          helperText={help}
          disabled={disabled}
        />
      );

    case 'number': {
      const number = value as number | undefined;
      const outside = number !== undefined && (number < field.min || number > field.max);

      return (
        <NumberField
          fullWidth
          size="small"
          label={t(field.label)}
          value={number}
          // Emptied, the key is taken off and the block draws its default, shown as the placeholder.
          onChange={set}
          placeholder={String(field.fallback)}
          allowNegative={false}
          error={outside}
          helperText={t('ReportDesign_Field_Range', [field.min, field.max])}
          disabled={disabled}
        />
      );
    }

    case 'toggle':
      return (
        <ToggleRow
          label={t(field.label)}
          description={help}
          checked={typeof value === 'boolean' ? value : field.fallback}
          onChange={set}
          disabled={disabled}
        />
      );

    case 'select':
      return (
        <TextField
          select
          fullWidth
          size="small"
          label={t(field.label)}
          helperText={help}
          value={field.options.some((option) => option.value === value) ? value : field.fallback}
          onChange={(event) => set(event.target.value)}
          disabled={disabled}
        >
          {field.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label ? t(option.label) : option.value}
            </MenuItem>
          ))}
        </TextField>
      );

    case 'image':
      return (
        <ImageField
          label={t(field.label)}
          help={help}
          value={value as ReportImageRef | null | undefined}
          onChange={set}
          images={images}
          disabled={disabled}
        />
      );

    case 'fieldItems':
      return (
        <FieldItemsEditor items={(value as ReportFieldItem[] | undefined) ?? []} onChange={set} disabled={disabled} />
      );

    default:
      return (
        <SignatureLinesEditor lines={value as ReportSignatureLine[] | undefined} onChange={set} disabled={disabled} />
      );
  }
};

export default BlockFieldControl;
